<template>
  <section 
    class="container-fluid d-flex justify-content-center px-3 dinn-font"
    :class="{
      'product-section-fem': isFemProduct,
      'product-section-other': product && !isFemProduct && !product.limited_edition,
      'product-section-limited_edition': product && product.limited_edition
    }">
    <!-- Loading State -->
    <div v-if="isLoading" class="container row col-12 d-flex justify-content-center mt-5 pt-5">
      <div class="col-lg-6">
        <div class="skeleton-image"></div>
      </div>
      <div class="col-lg-6 my-4 my-lg-0">
        <div class="skeleton-content">
          <div class="skeleton-title"></div>
          <div class="skeleton-tags d-flex gap-2">
            <div class="skeleton-tag"></div>
            <div class="skeleton-tag"></div>
          </div>
          <div class="skeleton-description"></div>
          <div class="skeleton-specifics d-flex gap-4">
            <div class="skeleton-specific" v-for="i in 4" :key="i"></div>
          </div>
          <div class="skeleton-price"></div>
          <div class="skeleton-button"></div>
        </div>
      </div>
    </div>

    <!-- Product Content -->
    <div v-else-if="product"
    :class="{'bg-dark': product && !product.limited_edition}" 
       class=" my-4 px-4 container row col-lg-10 col-md-7 pb-5 d-flex justify-content-between mt-5 pt-5 product-details-container">
      <div class="col-lg-6 px-0 pe-lg-4 product-img-wrapper d-flex flex-column justify-content-start">
        <MediaCarousel :items="productMedia" :label="product.name" fallback-url="/LOGO.svg" />
        <p v-if="!isMerchProduct" class="fw-bold fs-3 mt-3">{{ formatType(product.type) }} / FEMINIZADA</p>
      </div>
      <div class="col-lg-6 my-4 my-lg-0 px-0">
        <!-- edit btn -->
        <router-link v-if="isAdmin" :to="`/panel/productos/editar/${product.id}`" class="btn btn-primary mb-3">
          <i class="fas fa-edit me-2"></i>
          Editar Producto
        </router-link>
        <div class="d-flex w-100 flex-column">
          <h1 class="product-details-title fw-semibold dinn-font text-uppercase">{{ product.name }}</h1>
          <span class="w-100 py-1 mb-3 bg-orange"></span>
        </div>
        <div class="d-flex flex-column tags gap-2 mb-2 fs-4 me-lg-5 pe-lg-5">
          <!-- Show cannabis-specific info only for non-merch products -->
          <template v-if="!isMerchProduct">
            <p class="mb-3 fs-2">
              {{ product.genotype }}
            </p>
            <div class="mb-1 d-flex gap-4 row justify-content-between" v-if="product.indica || product.sativa">
              <span class="fw-lightbold col-5">INDICA: {{ product.indica }} </span>
              <span class="fw-lightbold col-5">SATIVA: {{ product.sativa }}</span>
            </div>
          </template>
          <div class="mb-1 d-flex gap-4 justify-content-between row" v-if="!isMerchProduct && (product.thc || product.cbd)">
            <span class="fw-lightbold col-5">THC: {{ product.thc }} </span>
            <span class="fw-lightbold col-5">CBD: {{ product.cbd }}</span>
          </div>
          <div class="mb-1 d-flex justify-content-between gap-4 row" v-if="product.yield_indoor || product.yield_outdoor">
            <span class="fw-lightbold col-5">PROD/INT: {{ product.yield_indoor }} </span>
            <span class="fw-lightbold col-5">PROD/EXT: {{ product.yield_outdoor }}</span>
          </div>
          <p class="mb-1 d-flex gap-4" v-if="product.flowering_time">
            <span class="fw-lightbold">TIEMPO DE FLORACIÓN: {{ product.flowering_time }}</span>
          </p>
          <p class="mb-1" v-if="!isMerchProduct && product.flavors">
            <span class="fw-lightbold">AROMAS:</span> {{ product.flavors }}
          </p>
          <div class="bg-white-2 p-3 text-black fw-light fs-5" v-if="product.description">
            <div v-if="sectionActive === 'description'">
              <div v-html="formattedFullDescription"></div>
            </div>
            <div v-else>
              <div v-html="formattedTruncatedDescription"></div>
              <button v-if="product.description.length > 300" @click="showFullDescription" class="btn btn-link p-0 fst-italic fw-light">
                Leer más
              </button>
            </div>
          </div>
          
          <div>
            <div class="mt-4 fs-4" v-if="!isMerchProduct">
              <p v-if="product.blisters && product.blisters.length > 0" class="fs-1 fw-bold adumu-font text-blue">
                X{{ product.blisters[0].seed_count }} {{ product.name === 'Orange Diesel' ? '+1 SEMILLAS' : 'SEMILLAS' }}
              </p>
              <p v-else-if="!isCatalogOnly" class="text-warning">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Información de blíster no disponible
              </p>
            </div>
            
          </div>
        </div>
        <div v-if="isMerchProduct">
          <p class="fs-1 fw-bold adumu-font text-blue">
            {{ product.product_size }}
          </p>
        </div>
        <button type="button" class="btn btn-primary btn-lg" @click="handleAddToCart">
          <i class="fas fa-shopping-cart me-2"></i>
          Comprar
        </button>
      </div>
      
    </div>

    <!-- No Product Found -->
    <div v-else class="container row col-12 d-flex justify-content-center mt-5 pt-5">
      <div class="text-center">
        <i class="fas fa-exclamation-circle fa-3x text-warning mb-3"></i>
        <h3>No se pudo cargar el producto</h3>
        <p class="text-muted" role="alert">{{ loadError || 'El producto que buscás no existe o no está disponible.' }}</p>
        <button type="button" class="btn btn-outline-primary me-2 mt-3" @click="loadProduct($route.params.id)">Reintentar</button>
        <router-link to="/productos" class="btn btn-primary mt-3">
          <i class="fas fa-arrow-left me-2"></i>
          Volver a Productos
        </router-link>
      </div>
    </div>
  </section>
  <!-- Related Products Section -->
  <section 
    v-if="product && (relatedLoading || relatedProducts.length > 0)"
    class="related-products">
    <img
      class="related-products__background"
      src="@/assets/images/gallery/gallery-intro-bg-rectangle.png"
      alt=""
      aria-hidden="true">
    <div class="container px-3">
      <h2 class="related-products__title text-white mb-3">Productos relacionados</h2>
      <p class="related-products__intro text-white-50 mb-4">Explorá variedades con genética, aromas o tipo similares.</p>
      <div class="row g-4 d-flex justify-content-center">
        <template v-if="relatedLoading">
          <div v-for="index in 4" :key="index" class="col-12 col-sm-6 col-lg-3">
            <div class="related-products__skeleton" role="status" aria-label="Cargando productos relacionados">
              <div class="related-products__skeleton-image"></div>
              <div class="related-products__skeleton-line"></div>
              <div class="related-products__skeleton-line related-products__skeleton-line--short"></div>
            </div>
          </div>
        </template>
        <div v-for="relatedProduct in (relatedLoading ? [] : relatedProducts)" :key="relatedProduct.id" class="col-12 col-sm-6 col-lg-3">
          <ProductCard :product="relatedProduct" class="related-product-card" />
        </div>
      </div>
      <div class="d-flex justify-content-center flex-column align-items-center">
        <p class="text-white mt-5 fw-light">¿Quieres ver todas las genéticas?</p>
        <router-link to="/productos" class="btn btn-primary mt-2">
          <i class="fas fa-store me-2"></i>
          Catálogo
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { getProduct, getRelatedProducts, onProductsChanged } from "@/services/products";
import { isAdminSession } from '@/utils/adminFreshness';
import { productImageUrls } from '@/composables/useProductImages';
import MediaCarousel from '@/components/media/MediaCarousel.vue';
import ProductCard from "@/components/ProductCard.vue";
import { formatPrice } from "@/helpers/product";
import { isAuthenticated } from "@/services/auth";

export default {
  name: "ProductDetails",
  components: {
    ProductCard, MediaCarousel
  },
  setup() {
    const router = useRouter();
    const isAdmin = localStorage.getItem('isAdmin') == 'true';
    const isUserAuthenticated = computed(() => isAuthenticated());
    const product = ref(null);
    const sectionActive = ref('technical-sheet');
    const isLoading = ref(true);
    const loadError = ref('');
    const relatedProducts = ref([]);
    const relatedLoading = ref(false);
    let relatedRequestId = 0;
    const selectedBlister = ref(null);

    const formatType = (type) => {
      const types = {
        'fem': 'Fotoperiódica',
        'auto': 'Autofloreciente',
        'reg': 'Regular',
        'merch': 'Merchandising'
      };
      return types[type] || type;
    };
    
    const isMerchProduct = computed(() => {
      return product.value && product.value.type === 'merch';
    });
    
    const isFemProduct = computed(() => {
      return product.value && product.value.type === 'fem';
    });

    const isCatalogOnly = computed(() => product.value?.catalog_only === true);

    const handleAddToCart = () => {
      if (!isUserAuthenticated.value) {
        router.push({ name: 'LoginForm', query: { redirect: '/panel/pedido-rapido' } });
        return;
      }

      router.push('/panel/pedido-rapido');
    };

    const technicalData = computed(() => {
      if (!product.value) return {};
      
      // Base technical data that's common to all product types
      const baseData = {
        'Banco': { 
          value: product.value.brand,
          icon: 'fas fa-building'
        },
        'Tipo': { 
          value: formatType(product.value.type),
          icon: isMerchProduct.value ? 'fas fa-tshirt' : 'fas fa-cannabis'
        },
      };

      if (!isCatalogOnly.value) {
        baseData['Stock'] = {
          value: product.value.stock,
          icon: 'fas fa-box'
        };
      }
      
      // Only add cannabis-specific fields if it's not a merch product
      if (!isMerchProduct.value) {
        return {
          ...baseData,
          'Genotipo': { 
            value: product.value.genotype,
            icon: 'fas fa-dna'
          },
          'Composición': { 
            value: product.value.sativa && product.value.indica ? 
              `${product.value.sativa}% Sativa / ${product.value.indica}% Indica` : null,
            icon: 'fas fa-percentage'
          },
          'Efectos': { 
            value: product.value.effects,  
            icon: 'fas fa-brain'
          },
          'Sabores': { 
            value: product.value.flavors,
            icon: 'fas fa-ice-cream'
          },
          'THC': { 
            value: product.value.thc,
            icon: 'fas fa-bolt'
          },
          'CBD': { 
            value: product.value.cbd,
            icon: 'fas fa-pills'
          },
          'Tiempo de Floración': { 
            value: product.value.flowering_time,
            icon: 'fas fa-clock'
          },
          'Ciclo Completo': { 
            value: product.value.completed_cicle,
            icon: 'fas fa-clock'
          },
          'Altura Interior': { 
            value: product.value.height_indoor,
            icon: 'fas fa-ruler-vertical'
          },
          'Altura Exterior': { 
            value: product.value.height_outdoor,
            icon: 'fas fa-mountain'
          },
          'Producción Interior': { 
            value: product.value.yield_indoor,
            icon: 'fas fa-warehouse'
          },
          'Producción Exterior': { 
            value: product.value.yield_outdoor,
            icon: 'fas fa-sun'
          },
          'Semillas por Blíster': { 
            value: selectedBlister.value?.seed_count,
            icon: 'fas fa-seedling'
          }
        };
      }
      
      return baseData;
    });

    const resetRelatedProducts = () => {
      relatedRequestId += 1;
      relatedProducts.value = [];
      relatedLoading.value = false;
    };

    const loadRelatedProducts = async () => {
      if (!product.value) return;
      
      const productId = product.value.id;
      const requestId = ++relatedRequestId;
      relatedProducts.value = [];
      relatedLoading.value = true;
      try {
        const related = await getRelatedProducts(productId);
        if (relatedRequestId === requestId && product.value?.id === productId) relatedProducts.value = related;
      } catch (error) {
        console.error('Error loading related products:', error);
      } finally {
        if (relatedRequestId === requestId) relatedLoading.value = false;
      }
    };

    // Computed property for truncated description
    const truncatedDescription = computed(() => {
      if (!product.value?.description) return '';
      if (product.value.description.length <= 250) return product.value.description;
      return product.value.description.slice(0, 250) + '...';
    });

    // Method to show full description
    const showFullDescription = () => {
      sectionActive.value = 'description';
      // Scroll to description section smoothly
      document.querySelector('.section-btns')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    };

    const formattedDescription = (text) => {
      if (!text) return '';
      // Convertir saltos de línea en etiquetas <p>
      return text
        .split('\n')
        .filter(p => p.trim()) // Eliminar líneas vacías
        .map(p => `<p class="mb-3">${p.trim()}</p>`)
        .join('');
    };

    const formattedTruncatedDescription = computed(() => {
      if (!product.value?.description) return '';
      let text = product.value.description;
      if (text.length > 300) {
        const truncated = text.substr(0, 300);
        const lastSpace = truncated.lastIndexOf(' ');
        text = truncated.substr(0, lastSpace > 0 ? lastSpace : 300);
      }
      return formattedDescription(text);
    });

    const formattedFullDescription = computed(() => {
      if (!product.value?.description) return '';
      return formattedDescription(product.value.description);
    });

    // Add computed property for flavors list
    const flavorsList = computed(() => {
      if (!product.value?.flavors) return [];
      return product.value.flavors.split(',').map(flavor => flavor.trim());
    });

    // Watch for product changes to set initial blister
    watch(() => product.value, (newProduct) => {
      if (newProduct?.blisters?.length) {
        // Ordenar blisters por cantidad de semillas
        const sortedBlisters = [...newProduct.blisters].sort((a, b) => a.seed_count - b.seed_count);
        // Seleccionar el primer blister con stock
        selectedBlister.value = sortedBlisters.find(b => b.stock > 0) || sortedBlisters[0];
      }
    }, { immediate: true });

    const productMedia = computed(() => {
      if (!product.value) return [];
      const images = productImageUrls(product.value).map(url => ({ url, type: 'image' }));
      if (!images.length) {
        images.push({ url: '/LOGO.svg', type: 'image', fallback: true });
      }
      return [
        ...images,
        ...(product.value.videos || []).map(video => ({ ...video, type: 'video' })),
      ];
    });

    return {
      product,
      sectionActive,
      formatPrice,
      handleAddToCart,
      isAdmin,
      isUserAuthenticated,
      isLoading,
      loadError,
      technicalData,
      relatedProducts,
      relatedLoading,
      loadRelatedProducts,
      resetRelatedProducts,
      ProductCard,
      truncatedDescription,
      showFullDescription,
      formattedTruncatedDescription,
      formattedFullDescription,
      flavorsList,
      selectedBlister,
      formatType,
      isMerchProduct,
      isFemProduct,
      isCatalogOnly,
      productMedia,
    };
  },
  methods: {
    async loadProduct(id) {
      const requestId = (this.requestId || 0) + 1;
      this.requestId = requestId;
      this.isLoading = true;
      this.loadError = '';
      this.product = null;
      this.resetRelatedProducts();
      this.sectionActive = 'technical-sheet';
      try {
        const product = await getProduct(id);
        if (this.requestId !== requestId) return;
        this.product = product;
        document.title = 'Detalles del producto - ' + product.name;
        this.isLoading = false;
        this.loadRelatedProducts();
      } catch (error) {
        if (this.requestId === requestId) {
          this.loadError = error.message;
          document.title = 'Producto no disponible';
        }
      } finally {
        if (this.requestId === requestId) this.isLoading = false;
      }
    },
  },
  mounted() {
    this.loadProduct(this.$route.params.id);
    this.stopProductChanges = onProductsChanged(() => {
      if (isAdminSession()) this.loadProduct(this.$route.params.id);
    });
    this.refreshVisibleProduct = () => {
      if (document.visibilityState === 'visible' && isAdminSession()) this.loadProduct(this.$route.params.id);
    };
    document.addEventListener('visibilitychange', this.refreshVisibleProduct);
  },
  beforeUnmount() {
    this.requestId = (this.requestId || 0) + 1;
    this.resetRelatedProducts();
    this.stopProductChanges?.();
    document.removeEventListener('visibilitychange', this.refreshVisibleProduct);
  },
  watch: {
    '$route.params.id'(id) { if (id) this.loadProduct(id); },
  }
};
</script>

<style scoped>
.related-products {
  min-height: 0 !important;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px 0 50px;
  position: relative;
  z-index: 3;
  isolation: isolate;
  overflow: visible;
  margin-top: -50px;
  margin-bottom: 0;
}

.related-products__background {
  position: absolute;
  top: -100px;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: calc(100% + 100px);
  object-fit: cover;
  object-position: center;
  z-index: 0;
  pointer-events: none;
}

.related-products > .container {
  position: relative;
  z-index: 1;
}

.product-details-container,
.product-details-container :deep(*),
.related-products,
.related-products :deep(*) {
  border-radius: 0 !important;
}

section :deep(*) {
  border-radius: 0 !important;
}

.related-products .row {
  align-items: stretch;
  row-gap: 1.5rem;
}

.related-product-card {
  height: 100%;
}

.related-products__skeleton {
  height: 100%;
  padding: 0 0 1.25rem;
  background: #202020;
  overflow: hidden;
}

.related-products__skeleton-image {
  width: 100%;
  aspect-ratio: 1 / 1;
}

.related-products__skeleton-line {
  height: 1.25rem;
  margin: 1rem 1rem 0;
}

.related-products__skeleton-line--short {
  width: 55%;
  height: 0.9rem;
}

.related-products__skeleton-image,
.related-products__skeleton-line {
  background: linear-gradient(90deg, #292929 25%, #393939 50%, #292929 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.2s infinite linear;
}

.related-product-card :deep(.product-card),
.related-product-card :deep(.card) {
  height: 100%;
}

.related-product-card :deep(.card) {
  display: flex;
  flex-direction: column;
}

.related-product-card :deep(.card-img-wrapper) {
  height: auto;
  aspect-ratio: 1 / 1;
  flex: 0 0 auto;
}

.related-product-card :deep(.image-container) {
  height: 100%;
  min-height: 0;
}

.related-product-card :deep(.card-img) {
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.related-product-card :deep(.card-body) {
  min-height: 116px;
  display: flex;
  flex: 1 0 auto;
  align-items: center;
}

.product-details-title {
  margin: 0;
  font-size: clamp(2rem, 3.5vw, 3.5rem);
  line-height: 1.05;
  letter-spacing: 0.02em;
}

.product-details-container .tags {
  font-size: clamp(1rem, 1.25vw, 1.25rem) !important;
  line-height: 1.45;
}

.product-details-container .tags .fs-2 {
  font-size: clamp(1.35rem, 2vw, 1.9rem) !important;
  line-height: 1.2;
}

.product-details-container .bg-white-2 {
  font-size: clamp(1rem, 1.15vw, 1.15rem) !important;
  line-height: 1.55;
}

.product-details-container .text-blue.fs-1,
.product-details-container .fs-1 {
  font-size: clamp(1.45rem, 2.2vw, 2rem) !important;
  line-height: 1.2;
}

.related-products__title {
  font-size: clamp(1.6rem, 2.5vw, 2.4rem);
  line-height: 1.1;
}

.related-products__intro {
  font-size: clamp(0.95rem, 1.2vw, 1.15rem);
  line-height: 1.45;
}

.related-product-card :deep(.product-name) {
  font-size: clamp(1rem, 1.4vw, 1.35rem) !important;
  line-height: 1.2;
}

.related-product-card :deep(.brand) {
  font-size: clamp(0.75rem, 1vw, 0.9rem) !important;
}

@media (max-width: 575.98px) {
  .related-products {
    margin-top: -100px;
    padding-top: 96px;
    padding-bottom: 72px;
  }

  .related-products > .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .related-products .row {
    row-gap: 1rem;
  }

  .related-product-card :deep(.card-body) {
    min-height: 104px;
  }

  .product-details-container .tags .row {
    row-gap: 0.35rem;
  }
}
section{
  min-height: 100vh;
}

.product-section-fem {
  position: relative;
  z-index: 1;
  overflow: visible;
  background-image: url('@/assets/images/fondos/fondo-1.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.product-section-other {
  position: relative;
  z-index: 1;
  overflow: visible;
  background-image: url('@/assets/images/fondos/fondo-2.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.product-section-limited_edition {
  position: relative;
  z-index: 1;
  overflow: visible;
  background-image: url('@/assets/images/fondos/fondo-ediciones-limitadas.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
.product-container{
  background-color: var(--black-color-2);
}
/* Styles remain unchanged */
.tag {
  background-color: var(--black-color-2);
  color: var(--white-color-2);
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 500;
}

.tag-limited_edition {
  background-color: #ffc107;
  color: #212529;
}

.product-img-wrapper {
  min-width: 0;
  display: flex;
  justify-content: flex-start;
  position: relative;
}

.text-muted {
  opacity: 0.6;
}

.fst-italic {
  font-style: italic;
}

.section-btns .active {
  color: var(--white-color-2);
  border-radius: 10px;
  border: none;
}

.table {
  background-color: var(--black-color-3);
  color: var(--white-color-2);
}

tr {
  border: 1px solid transparent;
}

/* Skeleton Styles */
.skeleton-image {
  width: 100%;
  height: 600px;
  background: var(--black-color-2);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 20px;
}

.skeleton-content {
  padding: 1rem;
}

.skeleton-title {
  height: 48px;
  width: 80%;
  background: var(--black-color-2);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.skeleton-tags {
  margin: 1rem 0;
}

.skeleton-tag {
  height: 24px;
  width: 80px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-description {
  height: 100px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin: 2rem 0;
}

.skeleton-specifics {
  margin: 2rem 0;
}

.skeleton-specific {
  height: 80px;
  width: 80px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
}

.skeleton-price {
  height: 64px;
  width: 40%;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin: 2rem 0;
}

.skeleton-button {
  height: 48px;
  width: 200px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .skeleton-image {
    height: 300px;
  }
  
  .skeleton-specific {
    height: 60px;
    width: 60px;
  }
  .specific{
    min-width: 40px;
  }
}

/* Add these styles */
.zoom-container {
  overflow: hidden;
  position: relative;
  cursor: zoom-in;
  width: 100%;
  height: 100%;
}

.zoom-image {
  width: 100%;
  height: 100%;
  transition: transform 0.1s ease-out;
  object-fit: contain;
  object-position: center;
  display: block;
}

/* Remove or comment out the hover effect styles if they exist */
/* .zoom-container:hover .zoom-image {
  transform: scale(1.2);
} */

.description-wrapper {
  display: inline;
}

.description-wrapper :deep(p) {
  display: inline;
}
.btn-link {
  font-size: 1rem;
  margin-left: 0.25rem;
  vertical-align: baseline;
}
.bg-white-2{
  background-color: #a5a5a5;
}

.login-required-section {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.alert-info {
  background-color: rgba(13, 202, 240, 0.1);
  border-color: rgba(13, 202, 240, 0.2);
  color: #0dcaf0;
}

.alert-info {
  background-color: rgba(13, 202, 240, 0.1);
  border-color: rgba(13, 202, 240, 0.2);
  color: #0dcaf0;
}

  
.alert-warning {
  background-color: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

/* Estilos para el precio tachado (precio original) */
.old-price {
  text-decoration: line-through;
  color: #b0b0b0;
  font-size: 1rem;
  margin-right: 8px;
}

/* Estilos para el precio con descuento */
.discounted-price {
  color: #0dcaf0;
  font-weight: bold;
  font-size: 1.3rem;
}

/* Badge de descuento */
.discount-badge-wrapper {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
}

.discount-badge {
  background: #ffc107;
  color: #212529;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

/* Overlay para la imagen al hacer hover */
.img-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.08);
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 12px;
}

.image-container:hover .img-overlay {
  opacity: 1;
}

/* Select de blisters */
.blister-select {
  min-width: 80px;
  background: #23272b;
  color: #fff;
  border: 1px solid #444;
  border-radius: 6px;
  font-size: 0.95rem;
}

.blister-select.disabled {
  opacity: 0.5;
  pointer-events: none;
}

/* Icono de ojo para vista rápida */
.eye-icon {
  font-size: 1.2rem;
  cursor: pointer;
  transition: color 0.2s;
}

.eye-icon:hover {
  color: #0dcaf0;
}

/* Ajustes para el contenedor de la imagen principal */
.zoom-container {
  overflow: hidden;
  position: relative;
  width: 100%;
}

.zoom-image {
  width: 100%;
  transition: transform 0.2s;
  border-radius: 12px;
}

/* Skeleton loaders */
.skeleton-image,
.skeleton-content,
.skeleton-title,
.skeleton-tag,
.skeleton-description,
.skeleton-specific,
.skeleton-price,
.skeleton-button {
  background: var(--black-color-3);
  background-size: 200% 100%;
  animation: skeleton-loading 1.2s infinite linear;
  border-radius: 8px;
}

.skeleton-image {
  width: 100%;
  height: 320px;
  margin-bottom: 16px;
}

.skeleton-title {
  width: 60%;
  height: 28px;
  margin-bottom: 12px;
}

.skeleton-tags {
  margin-bottom: 12px;
}

.skeleton-tag {
  width: 60px;
  height: 20px;
  margin-right: 8px;
}

.skeleton-description {
  width: 100%;
  height: 60px;
  margin-bottom: 16px;
}

.skeleton-specific {
  width: 60px;
  height: 20px;
}

.skeleton-price {
  width: 40%;
  height: 28px;
  margin-bottom: 12px;
}

.skeleton-button {
  width: 120px;
  height: 36px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Stock indicator styles */
.text-danger {
  color: #dc3545 !important;
}

/* Product background styles */
.product-details-container {
  position: relative;
  z-index: 1;
  height: auto;
  min-height: 0;
}
@media (max-width: 992px) {
  .product-details-container {
    max-height: none;
  }
}

/* Estilos para el carrusel */
.carousel-arrows {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 2;
}

.carousel-arrow {
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin: 0 10px;
  pointer-events: auto;
  transition: all 0.3s ease;
  opacity: 0.7;
}

.carousel-arrow:hover {
  background: rgba(0, 0, 0, 0.7);
  opacity: 1;
}

.carousel-arrow-left {
  margin-left: 15px;
}

.carousel-arrow-right {
  margin-right: 15px;
}

.carousel-indicators {
  position: absolute;
  bottom: 15px;
  left: 0;
  width: 100%;
  z-index: 2;
}

.carousel-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  border: none;
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-indicator.active {
  background-color: white;
}

/* Estilos para los indicadores tipo punto */
.carousel-dots {
  display: flex;
  align-items: center;
}

.carousel-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-dot.active {
  background: white;
  transform: scale(1.2);
}

</style>
